// English and German for the web app. The keys are the English text, as in the apps' catalogs; German comes from
// `de.ts`, whose entries were taken from the apps' catalogs where the same key exists (docs/47, "Languages").
//
// `t("{count} selected", { count: 3 })`: values fill `{name}`; a value named `count` picks the plural form, for
// English from `EN_PLURALS` and for German from the entry's `one`/`other`. `EN` holds the English the CSV writes
// differently from its key.

import { useSyncExternalStore } from "react";
import { DE, EN, EN_PLURALS, type Entry } from "./de";

export type Language = "en" | "de";
/** The setting: a language, or the browser's. */
export type LanguageChoice = Language | "system";

const STORAGE_KEY = "todolix.language";
const listeners = new Set<() => void>();

function browserLanguage(): Language {
  const languages = typeof navigator === "undefined" ? [] : navigator.languages ?? [navigator.language];
  for (const tag of languages) {
    const base = tag.toLowerCase().split("-")[0];
    if (base === "de") return "de";
    if (base === "en") return "en";
  }
  return "en";
}

function storedChoice(): LanguageChoice {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === "en" || value === "de") return value;
  } catch {
    // Storage may be blocked; the browser's language then applies.
  }
  return "system";
}

let choice: LanguageChoice = storedChoice();
let language: Language = forcedLanguage() ?? (choice === "system" ? browserLanguage() : choice);

/** `?lang=de` in the address, for tests and pictures. */
function forcedLanguage(): Language | undefined {
  if (typeof location === "undefined") return undefined;
  const value = new URLSearchParams(location.search).get("lang");
  return value === "de" || value === "en" ? value : undefined;
}

export function currentLanguage(): Language {
  return language;
}

export function languageChoice(): LanguageChoice {
  return choice;
}

export function setLanguage(next: LanguageChoice): void {
  choice = next;
  try {
    if (next === "system") localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Kept for this visit only.
  }
  language = next === "system" ? browserLanguage() : next;
  if (typeof document !== "undefined") document.documentElement.lang = language;
  for (const listener of listeners) listener();
}

/** The locale for dates and numbers: the browser's when it speaks the same language, else the language's own. */
export function locale(): string {
  const browser = typeof navigator === "undefined" ? undefined : navigator.language;
  if (browser && browser.toLowerCase().startsWith(language)) return browser;
  return language === "de" ? "de-DE" : "en-GB";
}

/** Calls `listener` whenever the language changes; returns what stops it. */
export function onLanguageChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Re-renders a component when the language changes, and returns it. */
export function useLanguage(): Language {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => language,
  );
}

const pluralRules = new Map<Language, Intl.PluralRules>();

function form(entry: Entry, count: unknown): string {
  if (typeof entry === "string") return entry;
  let rules = pluralRules.get(language);
  if (!rules) {
    rules = new Intl.PluralRules(language);
    pluralRules.set(language, rules);
  }
  const category = typeof count === "number" ? rules.select(count) : "other";
  return (category === "one" ? entry.one : category === "zero" ? entry.zero ?? entry.other : entry.other) ?? entry.other;
}

export type Values = Record<string, string | number>;

/** The text for an English key in the current language, with its values filled in. */
export function t(key: string, values?: Values): string {
  const entry: Entry = language === "de" ? DE[key] ?? EN_PLURALS[key] ?? key : EN_PLURALS[key] ?? EN[key] ?? key;
  const text = form(entry, values?.count);
  if (!values) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = values[name];
    if (value === undefined) return whole;
    return typeof value === "number" ? value.toLocaleString(locale()) : value;
  });
}

/** Whether German has the key (the test of missing translations reads it). */
export function hasGerman(key: string): boolean {
  return DE[key] !== undefined;
}

/** Marks an English key that is translated later (`t(variable)`), so the test of missing German finds it. */
export function key(text: string): string {
  return text;
}
